import { useState } from 'react';
import type { FormEvent } from 'react';
import { PublicFormFields, Slug } from '@cloudsign/shared';
import type { Group, PublicForm, Site } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Alert, Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Dialog } from '../fields-notices/Dialog';
import { useFieldList } from '../fields-notices/api';
import { useSaveForm } from './api';

type Fields = PublicForm['fields'];

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

function ModeSelect({ label, value, options, onChange, hint }: { label: string; value: string; options: string[]; onChange: (v: string) => void; hint?: string }) {
  return (
    <SelectField label={label} value={value} hint={hint} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o} value={o}>
          {t(`w30.form.mode.${o}`)}
        </option>
      ))}
    </SelectField>
  );
}

/** Create or edit one public form. `form` null means create. */
export function FormDialog({ form, sites, groups, onClose }: { form: PublicForm | null; sites: Site[]; groups: Group[]; onClose: () => void }) {
  const toast = useToast();
  const save = useSaveForm(form?.id ?? null);
  const [name, setName] = useState(form?.name ?? '');
  const [slug, setSlug] = useState(form?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(form !== null);
  const [siteId, setSiteId] = useState(form?.siteId ?? '');
  const [groupId, setGroupId] = useState(form?.groupId ?? '');
  const [enabled, setEnabled] = useState(form?.enabled ?? true);
  const [requireApproval, setRequireApproval] = useState(form?.requireApproval ?? true);
  const [maxDaysAhead, setMaxDaysAhead] = useState(String(form?.maxDaysAhead ?? 90));
  const [intro, setIntro] = useState(form?.intro ?? '');
  const [thanks, setThanks] = useState(form?.thanks ?? '');
  const [consentText, setConsentText] = useState(form?.consentText ?? '');
  const [fields, setFields] = useState<Fields>(form?.fields ?? PublicFormFields.parse({}));
  const [tried, setTried] = useState(false);

  // Sign-in questions of the chosen site that this group is asked. Sensitive ones are never offered (the server refuses them too).
  const questions = useFieldList({ kind: 'site', id: siteId }, 'sign_in', false);
  const offered = (questions.data ?? []).filter((f) => siteId !== '' && groupId !== '' && f.groupIds.includes(groupId) && !f.sensitive);

  const days = Number(maxDaysAhead);
  const slugBad = !Slug.safeParse(slug).success;
  const daysBad = !Number.isInteger(days) || days < 1 || days > 365;
  const invalid = name.trim() === '' || slugBad || siteId === '' || groupId === '' || daysBad;

  const setField = <K extends keyof Fields>(k: K, v: Fields[K]) => setFields((f) => ({ ...f, [k]: v }));
  const toggleQuestion = (id: string, on: boolean) => setField('fieldIds', on ? [...fields.fieldIds, id] : fields.fieldIds.filter((x) => x !== id));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (invalid) return;
    const offeredIds = new Set(offered.map((f) => f.id));
    save.mutate(
      {
        name: name.trim(),
        slug,
        siteId,
        groupId,
        enabled,
        requireApproval,
        maxDaysAhead: days,
        intro,
        thanks,
        consentText,
        fields: { ...fields, fieldIds: fields.fieldIds.filter((id) => offeredIds.has(id)) },
      },
      {
        onSuccess: () => {
          toast(form ? t('w30.forms.saved') : t('w30.forms.created'));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog title={form ? t('w30.form.editTitle') : t('w30.form.createTitle')} onClose={onClose} wide>
      <form onSubmit={submit} noValidate className="space-y-5">
        {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('w30.form.name')}
            hint={t('w30.form.nameHint')}
            value={name}
            maxLength={120}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
            error={tried && name.trim() === '' ? t('error.validation') : undefined}
          />
          <TextField
            label={t('w30.form.slug')}
            hint={t('w30.form.slugHint')}
            value={slug}
            maxLength={40}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            error={tried && slugBad ? t('w30.form.slugInvalid') : undefined}
          />
          <SelectField
            label={t('w30.form.site')}
            value={siteId}
            onChange={(e) => {
              setSiteId(e.target.value);
              setFields((f) => ({ ...f, fieldIds: [] }));
            }}
            error={tried && siteId === '' ? t('w30.form.needSiteGroup') : undefined}
          >
            <option value="">{t('w30.form.choose')}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('w30.form.group')}
            hint={t('w30.form.groupHint')}
            value={groupId}
            onChange={(e) => {
              setGroupId(e.target.value);
              setFields((f) => ({ ...f, fieldIds: [] }));
            }}
            error={tried && groupId === '' ? t('w30.form.needSiteGroup') : undefined}
          >
            <option value="">{t('w30.form.choose')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('w30.form.maxDaysAhead')}
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={maxDaysAhead}
            onChange={(e) => setMaxDaysAhead(e.target.value)}
            error={tried && daysBad ? t('error.validation') : undefined}
          />
          <div className="space-y-3 self-end">
            <CheckboxField label={t('w30.form.enabled')} hint={t('w30.form.enabledHint')} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <CheckboxField
              label={t('w30.form.requireApproval')}
              hint={t('w30.form.requireApprovalHint')}
              checked={requireApproval}
              onChange={(e) => setRequireApproval(e.target.checked)}
            />
          </div>
        </div>

        <fieldset className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-semibold">{t('w30.form.fieldsLegend')}</legend>
          <p className="mb-3 text-sm text-slate-600">{t('w30.form.fieldsHint')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <ModeSelect label={t('w30.form.field.email')} value={fields.email} options={['optional', 'required']} onChange={(v) => setField('email', v as Fields['email'])} />
            <ModeSelect label={t('w30.form.field.phone')} value={fields.phone} options={['off', 'optional', 'required']} onChange={(v) => setField('phone', v as Fields['phone'])} />
            <ModeSelect label={t('w30.form.field.company')} value={fields.company} options={['off', 'optional', 'required']} onChange={(v) => setField('company', v as Fields['company'])} />
            <ModeSelect
              label={t('w30.form.field.host')}
              hint={t('w30.form.hostHint')}
              value={fields.host}
              options={['none', 'optional', 'required']}
              onChange={(v) => setField('host', v as Fields['host'])}
            />
          </div>
        </fieldset>

        <fieldset className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-semibold">{t('w30.form.questions')}</legend>
          <p className="mb-3 text-sm text-slate-600">{t('w30.form.questionsHint')}</p>
          {offered.length === 0 ? (
            <p className="text-sm text-slate-600">{t('w30.form.questionsNone')}</p>
          ) : (
            <div className="space-y-2">
              {offered.map((f) => (
                <CheckboxField key={f.id} label={f.label} checked={fields.fieldIds.includes(f.id)} onChange={(e) => toggleQuestion(f.id, e.target.checked)} />
              ))}
            </div>
          )}
        </fieldset>

        <div className="grid gap-4">
          <TextAreaField label={t('w30.form.intro')} hint={t('w30.form.introHint')} value={intro} max={4000} onChange={setIntro} />
          <TextAreaField label={t('w30.form.thanks')} hint={t('w30.form.thanksHint')} value={thanks} max={2000} onChange={setThanks} />
          <TextAreaField label={t('w30.form.consentText')} hint={t('w30.form.consentTextHint')} value={consentText} max={2000} onChange={setConsentText} />
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {form ? t('w30.form.save') : t('w30.form.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TextAreaField({ label, hint, value, max, onChange }: { label: string; hint: string; value: string; max: number; onChange: (v: string) => void }) {
  const id = `ta-${label.replace(/\W+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={max}
        value={value}
        aria-describedby={`${id}-hint`}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
      />
      <p id={`${id}-hint`} className="mt-1 text-xs text-slate-600">
        {hint}
      </p>
    </div>
  );
}
